import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchCurrentUser,
  fetchUserById,
  fetchUserProfile,
  updateUserProfile,
  type IUserUpdateData,
} from '../api/users'
import { useAuthStore } from '../data/useAuthStore'
import { pb } from '../pocketbase/Pocketbase'

// Re-export types
export type { IUserUpdateData } from '../api/users'

// ============================================================================
// Query Keys
// ============================================================================

export const userKeys = {
  all: ['users'] as const,
  current: () => [...userKeys.all, 'current'] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
}

// ============================================================================
// User Queries
// ============================================================================

/**
 * Fetch the current authenticated user
 * This syncs with Zustand store on success
 */
export function useCurrentUser() {
  const { setUser, setUserProfile } = useAuthStore()

  return useQuery({
    queryKey: userKeys.current(),
    queryFn: async () => {
      const user = await fetchCurrentUser()
      // Sync with Zustand store
      if (user) {
        setUser(user)
        // Also fetch and set the user profile
        const profile = await fetchUserProfile(user.id)
        setUserProfile(profile)
      }
      return user
    },
    enabled: pb.authStore.isValid,
  })
}

/**
 * Fetch a user by ID
 */
export function useUser(userId: string) {
  return useQuery({
    queryKey: userKeys.detail(userId),
    queryFn: () => fetchUserById(userId),
    enabled: !!userId,
  })
}

// ============================================================================
// User Mutations
// ============================================================================

/**
 * Update the current user's profile
 */
export function useUpdateCurrentUser() {
  const queryClient = useQueryClient()
  const { setUserProfile } = useAuthStore()

  return useMutation({
    mutationFn: async (updates: IUserUpdateData) => {
      const userId = pb.authStore.record?.id
      if (!userId) {
        throw new Error('Not authenticated')
      }
      return updateUserProfile(userId, updates)
    },
    onSuccess: (updatedProfile) => {
      // Update Zustand store with the updated profile
      setUserProfile(updatedProfile)
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: userKeys.current() })
      queryClient.invalidateQueries({ queryKey: userKeys.detail(updatedProfile.user) })
    },
  })
}
